import { StripePrice } from "@switcherstudio/switcher-api-client";
import { PlanDetails, PlanDetailsProps } from "../../PlanDetails";
import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo } from "react";
import { useGenericMultiSelect } from "components/generic-multiselect/useGenericMultiSelect";
import { ComponentItem } from "components/generic-multiselect/types";
import { Spinner } from "components/spinners/Spinner";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { userHasClaim } from "helpers/userHelper";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { ListButton } from "components/buttons/ListButton";
import dayjs from "dayjs";

const pausedSubscriptionsEnabled =
    import.meta.env.VITE_ENABLE_PAUSED_SUBSCRIPTIONS === "true";

export interface SelectPlanStepProps {
    prices: StripePrice[];
    loading: boolean;
    currentPlan: string;
    selectPlan: (plan: StripePrice) => void;
    onHandleSubmit: (func) => void;
    onCancel: () => void | Promise<void>;
    onPause: () => void;
}

export const SelectPlanStep = ({
    prices,
    loading,
    currentPlan,
    selectPlan,
    onHandleSubmit,
    onCancel,
    onPause
}: SelectPlanStepProps) => {
    const { t } = useTranslation();
    const { userInfo } = useSelector((s: RootState) => s.user);

    const { data: customer, loading: customerLoading } = useSwitcherClient(
        (client) => client.stripe_GetCustomer,
        {
            requestImmediately: true
        }
    );

    const subCancelledOrNonexistent = useMemo(
        () =>
            customer?.StripeSubscriptions?.[0]?.CanceledAt ||
            customer?.StripeSubscriptions?.[0]?.CancelAtPeriodEnd ||
            customer?.StripeSubscriptions?.length === 0,
        [customer]
    );

    /** Maths to calculate the savings of purchasing an annual sub (instead of monthly) */
    const savings = useMemo(() => {
        /** Filter for the correct plans */
        const businessYearly = prices?.filter(
            (price) =>
                price?.Name?.includes("Business") &&
                price?.RecurringInterval === "year"
        )?.[0]?.Amount;
        const businessMonthly = prices?.filter(
            (price) =>
                price?.Name?.includes("Business") &&
                price?.RecurringInterval === "month"
        )?.[0]?.Amount;
        const studioYearly = prices?.filter(
            (price) =>
                price?.Name?.includes("Studio") &&
                price?.RecurringInterval === "year"
        )?.[0]?.Amount;
        const studioMonthly = prices?.filter(
            (price) =>
                price?.Name?.includes("Studio") &&
                price?.RecurringInterval === "month"
        )?.[0]?.Amount;

        /** Multiply the cost of monthly sub by 12 to get annual cost of purchasing months.
         * Divide cost of purchasing monthly sub for a year by cost of actual annual sub.
         * Subtract from 1 to get the percentage saved.
         * Multiply by 100 to convert to percentage from decimal.
         */
        const businessMonthlyTimes12 = businessMonthly * 12;
        const businessPercent =
            (1 - businessYearly / businessMonthlyTimes12) * 100;

        const studioMonthlyTimes12 = studioMonthly * 12;
        const studioPercent = (1 - studioYearly / studioMonthlyTimes12) * 100;

        /** Return the values in an array as objects that will be accessed in the PlanDetails component */
        return [
            { name: "Business", percent: businessPercent },
            { name: "Studio", percent: studioPercent }
        ];
    }, [prices]);

    /**
     * If user account created after 9/24/24, no 7 Day Pass visible.
     * If user account created before 9/24/24, 7 Day Pass is
     * available for purchase only if they are not currently subscribed to a plan.
     * Otherwise, it's visible and  disabled.
     */
    const sevenDayPassState = useMemo<
        "active" | "inactive" | "unavailable"
    >(() => {
        if (userHasClaim("billing.onetimepass")) {
            if (
                userInfo?.Plan?.Details?.ActiveUntil === null ||
                !userInfo?.Plan?.Details?.IsRecurring
            ) {
                return "active";
            }
            return "inactive";
        }
        return "unavailable";
    }, [userInfo]);

    const sortPrices = useCallback((a: StripePrice, b: StripePrice) => {
        // Special case for Launch plans - always at the top
        if (a?.Name?.includes("Launch")) {
            return -1;
        }
        if (b?.Name?.includes("Launch")) {
            return 1;
        }

        // Special case for Pass plans - always at the bottom
        if (a?.Name?.includes("Pass")) return 1;
        if (b?.Name?.includes("Pass")) return -1;

        // First group by plan type (Studio vs Business)
        const aIsStudio = a?.Name?.includes("Studio");
        const bIsStudio = b?.Name?.includes("Studio");

        if (aIsStudio && !bIsStudio) return -1; // Studio comes before Business
        if (!aIsStudio && bIsStudio) return 1; // Business comes after Studio

        // Within the same plan type (Studio or Business), sort by interval (monthly first, then yearly)
        if (a?.RecurringInterval !== b?.RecurringInterval) {
            return a?.RecurringInterval === "month" ? -1 : 1;
        }

        return 0;
    }, []);

    const filterSevenDayPass = useMemo(() => {
        const sortedPrices = [...(prices ?? [])]?.sort(sortPrices);
        switch (sevenDayPassState) {
            case "active":
                return sortedPrices;
            case "inactive":
            case "unavailable":
                return sortedPrices?.filter((p) => !p?.Name.includes("Pass"));
            default:
                return sortedPrices;
        }
    }, [prices, sevenDayPassState, sortPrices]);

    const planDetailsComponentItems: ComponentItem<StripePrice>[] =
        useMemo(() => {
            if (!prices?.length) return [];

            return filterSevenDayPass.map((price: StripePrice, key) => {
                const sevenDayPassDisabled =
                    price?.Name?.includes("Pass") &&
                    sevenDayPassState === "inactive";

                return {
                    id: price?.Id,
                    component: (
                        <PlanDetails
                            item={price}
                            key={key}
                            percentSavings={savings.filter((s) =>
                                price?.Name?.includes(s?.name)
                            )}
                            disabled={sevenDayPassDisabled}
                        />
                    ),
                    baseObject: price
                };
            });
        }, [prices, savings, filterSevenDayPass, sevenDayPassState]);

    const memoizedPlan = useMemo(
        () => (currentPlan != "" ? [currentPlan] : []),
        [currentPlan]
    );

    const { GenericMultiSelectComponent, handleSubmit, selectedIdSet } =
        useGenericMultiSelect<StripePrice, PlanDetailsProps>({
            items: planDetailsComponentItems,
            onSubmit: ([id]) => {
                if (selectPlan) {
                    const selectedPlan = selectedIdSet.has(id);
                    if (selectedPlan) {
                        const selected = prices?.filter(
                            (p) => p?.Id === id
                        )?.[0];
                        selectPlan(selected);
                    }
                }
            },
            previouslySelectedIds: memoizedPlan,
            allowNoSelection: false,
            loading
        });

    /** handler to get function from here to parent */
    useEffect(() => {
        if (selectedIdSet.size > 0) {
            onHandleSubmit(() => {
                handleSubmit();
            });
        }
    }, [onHandleSubmit, currentPlan, prices, handleSubmit, selectedIdSet]);

    const isLoading: boolean = useMemo(
        () => loading || customerLoading,
        [loading, customerLoading]
    );

    const showPause = useMemo(() => {
        return (
            pausedSubscriptionsEnabled &&
            userInfo?.Plan?.Details?.RecurringInterval === "month" &&
            userInfo?.Plan?.Details?.Status !== "trialing"
        );
    }, [
        userInfo?.Plan?.Details?.RecurringInterval,
        userInfo?.Plan?.Details?.Status
    ]);

    const pauseIsDisabled = useMemo(() => {
        return (
            showPause &&
            dayjs()
                .add(2, "hours")
                .isAfter(dayjs(userInfo?.Plan?.Details?.ActiveUntil))
        );
    }, [userInfo?.Plan?.Details?.ActiveUntil, showPause]);

    return (
        <>
            {isLoading ? (
                <div className={styles["spinner-container"]}>
                    <Spinner />
                </div>
            ) : (
                !!filterSevenDayPass && (
                    <>
                        <div
                            className={`${styles["content"]} ${styles["disabled"]}`}
                        >
                            {GenericMultiSelectComponent}
                            {sevenDayPassState === "inactive" && (
                                <>
                                    <hr />
                                    <div className={styles["pass-disabled"]}>
                                        <PlanDetails
                                            item={prices?.find((p) =>
                                                p?.Name?.includes("Pass")
                                            )}
                                            disabled={true}
                                        />
                                    </div>
                                </>
                            )}
                            {!subCancelledOrNonexistent && (
                                <>
                                    {showPause ? (
                                        <>
                                            <hr />
                                            <ListButton
                                                onClick={onPause}
                                                disabled={pauseIsDisabled}
                                            >
                                                {t(`subscription:pause-plan`)}
                                            </ListButton>
                                            {pauseIsDisabled && (
                                                <div
                                                    className={`${styles["pause-disabled-message"]}`}
                                                >
                                                    {t(
                                                        `subscription:pause-disabled-message`
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <hr />
                                    )}
                                    <ListButton
                                        onClick={onCancel}
                                        variant="danger"
                                    >
                                        {t(`subscription:cancel-plan`)}
                                    </ListButton>
                                </>
                            )}
                        </div>
                    </>
                )
            )}
        </>
    );
};
